/**
 * The English layer's preferences — which translation, which transliteration, how big.
 *
 * NONE of these bump `audioEpoch`. Switching translation while a verse is sounding must
 * not restart it: the panel's text and the recitation are independent, and re-seeking
 * would make a one-tap preference feel destructive. That is why this slice takes `set`
 * rather than the `edit` the audio and repeat slices take — the same reason `display.ts`
 * does.
 */
import { DEFAULT_TRANSLATION } from '@mushaf/corpus'
import type { EnglishSlice, SettingsSet, TranslitScheme } from './types'

/**
 * The transliteration options, in the order the settings panel lists them.
 *
 * `installed: false` is shown and SELECTABLE rather than hidden. Hiding them would make
 * the app look as though one romanisation exists and this is it — when in fact the
 * recitation-aware family is the one a serious reader eventually wants, and the only
 * reason it is absent is an unresolved licence. Choosing one says so (see
 * `panel/Transliteration.tsx`) instead of silently falling back.
 */
export const TRANSLIT_OPTIONS: {
  id: TranslitScheme
  label: string
  hint: string
  installed: boolean
}[] = [
  { id: 'off', label: 'Off', hint: 'Arabic and translation only', installed: true },
  {
    id: 'syllabic',
    label: 'As recited',
    hint: 'Bismillaahir Rahmaanir Raheem — shows idgham and madd as a reciter says them',
    installed: true,
  },
  {
    id: 'simple',
    label: 'Letter by letter',
    hint: 'bismi llahi r-rahmani r-rahimi — follows the written script exactly',
    installed: true,
  },
  {
    id: 'scholarly',
    label: 'Letter by letter, with marks',
    hint: 'bismi llāhi r-raḥmāni r-raḥīmi — precise, but assumes you read the marks',
    installed: true,
  },
  {
    id: 'word',
    label: 'Per-word (unavailable)',
    hint: 'Its source forbids compiling a copy of the data. Not installable.',
    installed: false,
  },
]

/**
 * Latin text scale, independent of the Arabic one.
 *
 * Narrower bounds than `fontScale`'s 60–170: the Arabic page is solved for absolutely
 * and can take a wide range, whereas the panel is a fixed column of prose and anything
 * past ~140% reflows it to three words a line.
 */
export const ENGLISH_SCALE_MIN = 85
export const ENGLISH_SCALE_MAX = 140
export const ENGLISH_SCALE_STEP = 5
export const ENGLISH_SCALE_DEFAULT = 100

const clampScale = (n: number) =>
  Math.min(
    ENGLISH_SCALE_MAX,
    Math.max(ENGLISH_SCALE_MIN, Math.round(n / ENGLISH_SCALE_STEP) * ENGLISH_SCALE_STEP),
  )

export const createEnglishSlice = (set: SettingsSet): EnglishSlice => ({
  translation: DEFAULT_TRANSLATION,
  /**
   * `syllabic` — the sourced, recitation-aware edition.
   *
   * It is the default over the generated romanisation because it is the only scheme that
   * shows what a reciter actually does: idgham across a word boundary, the madd lengths,
   * the pausal form. The generated one reads the script faithfully and says, correctly,
   * that it cannot do those things (L29).
   *
   * The generated scheme remains the licence-free fallback, and the UI falls back to it
   * automatically if the asset is not installed — `pnpm translit:fetch` is a separate
   * step from `corpus:sync`, so a fresh clone will not have it.
   */
  translit: 'syllabic',
  englishScale: ENGLISH_SCALE_DEFAULT,
  panelExpanded: false,
  stripLine: 'means',

  setTranslation: (translation) => set({ translation }),
  setTranslit: (translit) => set({ translit }),
  setEnglishScale: (englishScale) => set({ englishScale: clampScale(englishScale) }),
  setPanelExpanded: (panelExpanded) => set({ panelExpanded }),
  setStripLine: (stripLine) => set({ stripLine }),
})
